/**
 * Garde d'accessibilité des fenêtres modales.
 *
 * Qui pose l'attribut ? SweetAlert2, et lui seul. Pour qu'un lecteur d'écran
 * ne voie que l'alerte, `setAriaHidden()` pose `aria-hidden="true"` sur chaque
 * enfant direct de `<body>` qui ne contient pas le conteneur de l'alerte —
 * donc sur `<div id="app">`, la racine de l'application. Reactstrap, lui, ne
 * touche jamais à `aria-hidden` : une fenêtre V1 fermée juste après une alerte
 * hérite simplement de l'oubli et semble en être la cause.
 *
 * Pourquoi l'attribut n'est-il pas retiré ? Le nettoyage symétrique
 * (`unsetAriaHidden()`) dépend de deux conditions qui ne survivent pas à une
 * fermeture interrompue :
 *
 *   1. `isModal()` — la classe `swal2-shown` doit encore être sur `<body>` au
 *      moment de la fermeture. Tout code qui réécrit `body.className` pendant
 *      qu'une alerte est ouverte fait sauter le nettoyage.
 *   2. l'événement `animationend` du popup — si le popup ou son conteneur est
 *      démonté avant la fin de l'animation de sortie (navigation, démontage
 *      React brutal), le nettoyage n'a jamais lieu.
 *
 * Les deux cas ont été reproduits au runtime (sweetalert2 11.26) : `#app`
 * conserve `aria-hidden="true"` alors qu'il ne reste aucun `[role="dialog"]`,
 * et toute la page devient invisible au lecteur d'écran jusqu'au rechargement.
 *
 * Le même nettoyage manqué laisse un second résidu, visible celui-là par tout
 * le monde : `swal2-shown` reste sur `<html>` et `<body>`, donc
 * `body { overflow: hidden }` — la page garde exactement l'état de
 * verrouillage qu'elle a pendant qu'une fenêtre est ouverte. S'y ajoutent, sur
 * les plateformes concernées, le décalage `swal2-iosfix` (Safari/iOS) et le
 * `padding-right` de compensation de la barre de défilement (Windows).
 *
 * Cette garde rétablit l'invariant à la source : aucune fenêtre ouverte ⇒ ni
 * `aria-hidden` ni verrouillage hérités d'une fenêtre. Elle couvre d'un coup
 * les deux moteurs de fenêtres encore utilisés dans le shell V2 (SweetAlert2
 * et reactstrap) et n'a rien à savoir de chaque appelant.
 *
 * Limite assumée : la garde retire l'attribut de tout enfant direct de
 * `<body>` qui n'en portait pas au démarrage de l'application. Les enfants de
 * `<body>` sont `#app` et les portails (fenêtres, alertes, menus `react-select`,
 * bulle Rafiki) ; aucun code de l'application n'y pose `aria-hidden`, et les
 * valeurs mémorisées par SweetAlert2 dans `data-previous-aria-hidden` sont
 * rétablies telles quelles.
 */

const ARIA_HIDDEN = "aria-hidden";

/** Mémoire de SweetAlert2 : valeur de `aria-hidden` d'avant l'ouverture. */
const PREVIOUS_ARIA_HIDDEN = "data-previous-aria-hidden";

const DIALOG_SELECTOR = "[role='dialog'], [role='alertdialog']";

/** Classes d'état que SweetAlert2 pose sur `<html>` et `<body>`. */
const SWAL_STATE_CLASSES = Object.freeze([
  "swal2-shown",
  "swal2-height-auto",
  "swal2-no-backdrop",
  "swal2-toast-shown",
]);

/** Décalage de la page appliqué par `iOSfix()` sur Safari et iOS. */
const SWAL_IOS_FIX_CLASS = "swal2-iosfix";

/**
 * Preuve qu'une alerte est encore vivante. C'est le popup qui fait foi, pas le
 * conteneur — une fermeture interrompue peut laisser un conteneur vide — ni
 * `[role="dialog"]` : un toast pose les mêmes classes mais s'annonce
 * `role="alert"`. Pendant l'animation de sortie le popup est toujours là, donc
 * SweetAlert2 garde la main jusqu'au bout quand il le peut.
 */
const SWAL_POPUP = ".swal2-popup";

/**
 * Une fenêtre laissée montée mais masquée ne compte pas comme ouverte :
 * reactstrap garde son portail dans le DOM avec `display: none` quand
 * `unmountOnClose` est désactivé.
 */
export function isDialogOpen(element) {
  if (!element || !element.isConnected) return false;
  const view = element.ownerDocument?.defaultView;
  for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
    if (node.hidden || node.style?.display === "none") return false;
    const computed = view?.getComputedStyle?.(node);
    if (computed && (computed.display === "none" || computed.visibility === "hidden")) return false;
  }
  return true;
}

/** Y a-t-il encore une fenêtre visible dans la page ? */
export function hasOpenDialog(documentRef) {
  const candidates = documentRef?.querySelectorAll?.(DIALOG_SELECTOR);
  if (!candidates) return false;
  return Array.from(candidates).some(isDialogOpen);
}

/**
 * Rejoue le nettoyage que SweetAlert2 a sauté, à l'identique : la valeur
 * mémorisée dans `data-previous-aria-hidden` prime, sinon l'attribut part.
 */
const restore = (child, keep) => {
  if (child.hasAttribute(PREVIOUS_ARIA_HIDDEN)) {
    const previous = child.getAttribute(PREVIOUS_ARIA_HIDDEN);
    child.removeAttribute(PREVIOUS_ARIA_HIDDEN);
    if (previous) child.setAttribute(ARIA_HIDDEN, previous);
    else child.removeAttribute(ARIA_HIDDEN);
    return true;
  }
  if (child.getAttribute(ARIA_HIDDEN) !== "true" || keep.has(child)) return false;
  child.removeAttribute(ARIA_HIDDEN);
  return true;
};

const scrollLockRoots = (documentRef) => [documentRef.documentElement, documentRef.body].filter(Boolean);

/**
 * `style.paddingRight = ""` ne vide pas la propriété partout (composante d'un
 * raccourci) : `removeProperty` est la seule écriture fiable.
 */
const setStyle = (element, property, value) => {
  if (value) element.style.setProperty(property, value);
  else element.style.removeProperty(property);
};

/** Reste-t-il un verrouillage sans alerte vivante pour le justifier ? */
export function hasScrollLockResidue(documentRef) {
  if (!documentRef?.body || documentRef.querySelector(SWAL_POPUP)) return false;
  return scrollLockRoots(documentRef).some(
    (element) =>
      element.classList.contains(SWAL_IOS_FIX_CLASS) ||
      SWAL_STATE_CLASSES.some((name) => element.classList.contains(name))
  );
}

/**
 * Rejoue `removeBodyClasses()` puis `undoIOSfix()` : le décalage de la page est
 * repris dans `body.style.top`, donc restituable à l'identique.
 */
const releaseScrollLock = (documentRef) => {
  scrollLockRoots(documentRef).forEach((element) => element.classList.remove(...SWAL_STATE_CLASSES));
  const { body } = documentRef;
  if (!body.classList.contains(SWAL_IOS_FIX_CLASS)) return;
  const offset = parseInt(body.style.top, 10);
  body.classList.remove(SWAL_IOS_FIX_CLASS);
  setStyle(body, "top", "");
  if (Number.isFinite(offset)) body.scrollTop = -offset;
};

export function createDialogAriaHiddenGuard(options = {}) {
  const documentRef = options.documentRef || (typeof document === "undefined" ? null : document);
  /** Racine de l'application : observée pour ce qu'elle porte, jamais pour son contenu. */
  let root = null;
  /**
   * Enfants de `<body>` déjà masqués au démarrage, avant qu'aucune fenêtre
   * n'ait pu s'ouvrir : eux seuls gardent leur `aria-hidden`. Le relevé n'est
   * pas rafraîchi ensuite — sinon un attribut oublié finirait mémorisé comme
   * légitime si l'ouverture et la fermeture tombaient dans le même lot de
   * mutations.
   */
  let keep = new Set();
  /** `padding-right` du `<body>` au démarrage : la compensation de scrollbar y revient. */
  let bootPaddingRight = "";
  let scheduled = false;
  let bodyObserver = null;
  let portalObserver = null;

  /** @returns {Element[]} les enfants dont l'attribut oublié a été retiré. */
  const reconcile = () => {
    if (!documentRef?.body) return [];
    // Le balayage des fenêtres de la page ne coûte que lorsqu'il y a quelque
    // chose à rendre : les enfants de `<body>` se comptent sur les doigts.
    const masked = Array.from(documentRef.body.children).filter(
      (child) =>
        child.hasAttribute(PREVIOUS_ARIA_HIDDEN) ||
        (child.getAttribute(ARIA_HIDDEN) === "true" && !keep.has(child))
    );
    const locked = hasScrollLockResidue(documentRef);
    if ((masked.length === 0 && !locked) || hasOpenDialog(documentRef)) return [];
    if (locked) releaseScrollLock(documentRef);
    // Les deux résidus signent le même nettoyage manqué : la compensation de
    // barre de défilement, sautée avec eux, revient à sa valeur de démarrage.
    if (documentRef.body.style.paddingRight !== bootPaddingRight) {
      setStyle(documentRef.body, "padding-right", bootPaddingRight);
    }
    return masked.filter((child) => restore(child, keep));
  };

  /**
   * Une fenêtre peut disparaître sans toucher à `<body>` : son popup est retiré
   * de son portail, ou le portail lui-même passe en `display: none`. On suit
   * donc chaque portail, mais jamais la racine de l'application — observer son
   * sous-arbre reviendrait à observer toute l'interface à chaque rendu.
   */
  const watchPortals = () => {
    portalObserver.disconnect();
    Array.from(documentRef.body.children).forEach((child) => {
      if (child === root) return;
      portalObserver.observe(child, {
        attributeFilter: ["class", "hidden", "role", "style"],
        attributes: true,
        childList: true,
        subtree: true,
      });
    });
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    Promise.resolve().then(() => {
      scheduled = false;
      watchPortals();
      reconcile();
    });
  };

  const start = () => {
    if (bodyObserver || !documentRef?.body) return;
    root = options.root || documentRef.getElementById?.("app") || null;
    keep = new Set(Array.from(documentRef.body.children).filter((child) => child.getAttribute(ARIA_HIDDEN) === "true"));
    bootPaddingRight = documentRef.body.style.paddingRight;
    bodyObserver = new MutationObserver(schedule);
    bodyObserver.observe(documentRef.body, { attributeFilter: ["class"], attributes: true, childList: true });
    bodyObserver.observe(documentRef.documentElement, { attributeFilter: ["class"], attributes: true });
    portalObserver = new MutationObserver(schedule);
    watchPortals();
    reconcile();
  };

  const stop = () => {
    bodyObserver?.disconnect();
    portalObserver?.disconnect();
    bodyObserver = null;
    portalObserver = null;
  };

  return { reconcile, start, stop };
}

/** Installe la garde pour toute la durée de vie de l'application. */
export function installDialogAriaHiddenGuard(options) {
  const guard = createDialogAriaHiddenGuard(options);
  guard.start();
  return guard.stop;
}
